import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  Shield, 
  Users,
  Mail,
  ChevronRight,
  ArrowLeft,
  UserMinus,
  Crown,
  Trash2,
  RefreshCw
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "react-hot-toast";

export default function Teachers({ user }) {
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDemoteDialog, setShowDemoteDialog] = useState(false);
  const [showRemoveSuperAdminDialog, setShowRemoveSuperAdminDialog] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [demoting, setDemoting] = useState(false);
  const [removingSuperAdmin, setRemovingSuperAdmin] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);

  useEffect(() => {
    fetchTeachers();
  }, []);

  const fetchTeachers = async () => {
    try {
      const response = await axios.get(`${API}/users/teachers`);
      setTeachers(response.data);
    } catch (error) {
      console.error("Error fetching teachers:", error);
      toast.error("Failed to load teachers");
    } finally {
      setLoading(false);
    }
  };

  const demoteToStudent = async () => {
    if (!selectedTeacher) return;
    
    setDemoting(true);
    try {
      await axios.put(`${API}/users/${selectedTeacher.user_id}/demote-to-student`);
      toast.success(`${selectedTeacher.name} has been demoted to student`);
      setShowDemoteDialog(false);
      setSelectedTeacher(null);
      fetchTeachers();
    } catch (error) {
      console.error("Error demoting user:", error);
      toast.error(error.response?.data?.detail || "Failed to demote user");
    } finally {
      setDemoting(false);
    }
  };

  const removeSuperAdmin = async () => {
    if (!selectedTeacher) return;
    
    setRemovingSuperAdmin(true);
    try {
      await axios.put(`${API}/users/${selectedTeacher.user_id}/remove-super-admin`);
      toast.success(`${selectedTeacher.name} is now a regular teacher`);
      setShowRemoveSuperAdminDialog(false);
      setSelectedTeacher(null);
      fetchTeachers();
    } catch (error) {
      console.error("Error removing super admin:", error);
      toast.error(error.response?.data?.detail || "Failed to remove super admin status");
    } finally {
      setRemovingSuperAdmin(false);
    }
  };

  const runCleanup = async () => {
    setCleaningUp(true);
    setCleanupResult(null);
    try {
      const response = await axios.post(`${API}/admin/cleanup-orphaned-data`);
      setCleanupResult(response.data.deleted);
      const { messages, journals, submissions } = response.data.deleted;
      const total = messages + journals + submissions;
      if (total > 0) {
        toast.success(`Cleanup complete! Removed ${messages} messages, ${journals} journals, ${submissions} submissions`);
      } else {
        toast.success("Cleanup complete! No orphaned data found.");
      }
    } catch (error) {
      console.error("Error running cleanup:", error);
      toast.error(error.response?.data?.detail || "Failed to run cleanup");
    } finally {
      setCleaningUp(false);
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };

  // Check if current user is super admin
  const isSuperAdmin = user?.is_super_admin;

  if (!isSuperAdmin) {
    return (
      <Layout user={user}>
        <div className="text-center py-16">
          <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600">Access denied. Super Admin only.</p>
          <Button variant="link" onClick={() => navigate('/teacher')}>
            Back to Dashboard
          </Button>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-slate-300 border-t-amber-500 rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div data-testid="teachers-page" className="space-y-8">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/teacher')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-serif text-2xl md:text-3xl font-bold text-slate-900">
                Manage Teachers
              </h1>
              <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">
                <Crown className="w-3 h-3 mr-1" />
                Super Admin
              </Badge>
            </div>
            <p className="text-slate-600 mt-1">
              View and manage teacher/admin accounts
            </p>
          </div>
        </div>

        {/* Stats */}
        <Card className="border-slate-100">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Total Teachers</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{teachers.length}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Database Cleanup */}
        <Card className="border-slate-100">
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <Trash2 className="w-5 h-5 mr-2 text-red-500" />
              Database Cleanup
            </CardTitle>
            <CardDescription>
              Remove orphaned data from deleted users (messages, journals, submissions)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-600">
                {cleanupResult ? (
                  <div className="space-y-1">
                    <p>Last cleanup removed:</p>
                    <ul className="text-slate-500 ml-4">
                      <li>• {cleanupResult.messages} orphaned messages</li>
                      <li>• {cleanupResult.journals} orphaned journals</li>
                      <li>• {cleanupResult.submissions} orphaned submissions</li>
                    </ul>
                  </div>
                ) : (
                  <p>Run cleanup to remove data from deleted users that wasn&apos;t properly cleaned up.</p>
                )}
              </div>
              <Button
                onClick={runCleanup}
                disabled={cleaningUp}
                variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50"
              >
                {cleaningUp ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Cleaning...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Run Cleanup
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Teachers List */}
        {teachers.length === 0 ? (
          <Card className="border-slate-100">
            <CardContent className="py-16 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">No teachers found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {teachers.map((teacher) => {
              const isCurrentUser = teacher.user_id === user.user_id;
              const isSuperAdminTeacher = teacher.is_super_admin;
              
              return (
                <Card 
                  key={teacher.user_id}
                  className={`border-slate-100 ${isSuperAdminTeacher ? 'border-purple-200 bg-purple-50/30' : ''}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Avatar className="w-12 h-12">
                          <AvatarImage src={teacher.picture} />
                          <AvatarFallback className={isSuperAdminTeacher ? 'bg-purple-100 text-purple-700' : 'bg-amber-100 text-amber-700'}>
                            {getInitials(teacher.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-lg text-slate-900">
                              {teacher.name}
                            </h3>
                            {isSuperAdminTeacher && (
                              <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">
                                <Crown className="w-3 h-3 mr-1" />
                                Super Admin
                              </Badge>
                            )}
                            {isCurrentUser && (
                              <Badge variant="outline">You</Badge>
                            )}
                          </div>
                          <p className="text-slate-500 text-sm flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {teacher.email}
                          </p>
                          {teacher.created_at && (
                            <p className="text-xs text-slate-500 mt-1">
                              Joined {format(new Date(teacher.created_at), 'MMM d, yyyy')}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isSuperAdminTeacher && !isCurrentUser && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedTeacher(teacher);
                              setShowRemoveSuperAdminDialog(true);
                            }}
                            className="text-purple-600 border-purple-200 hover:bg-purple-50"
                          >
                            <Crown className="w-4 h-4 mr-1" />
                            Remove Super Admin
                          </Button>
                        )}
                        {!isSuperAdminTeacher && !isCurrentUser && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedTeacher(teacher);
                              setShowDemoteDialog(true);
                            }}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <UserMinus className="w-4 h-4 mr-1" />
                            Demote
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Demote Confirmation Dialog */}
      <Dialog open={showDemoteDialog} onOpenChange={setShowDemoteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <UserMinus className="w-5 h-5" />
              Demote to Student
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to demote <strong>{selectedTeacher?.name}</strong> from Teacher to Student?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-800">
                <strong>This action will:</strong>
              </p>
              <ul className="text-sm text-red-700 mt-2 space-y-1 list-disc list-inside">
                <li>Remove all admin privileges from this user</li>
                <li>Revoke ability to create assignments and review submissions</li>
                <li>Move them to the Students list</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDemoteDialog(false);
                setSelectedTeacher(null);
              }}
              disabled={demoting}
            >
              Cancel
            </Button>
            <Button
              onClick={demoteToStudent}
              disabled={demoting}
              className="bg-red-600 hover:bg-red-700"
            >
              {demoting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                  Demoting...
                </>
              ) : (
                <>
                  <UserMinus className="w-4 h-4 mr-2" />
                  Confirm Demote
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Super Admin Confirmation Dialog */}
      <Dialog open={showRemoveSuperAdminDialog} onOpenChange={setShowRemoveSuperAdminDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-600">
              <Crown className="w-5 h-5" />
              Remove Super Admin Status
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to remove super admin status from <strong>{selectedTeacher?.name}</strong>?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-800">
                <strong>This action will:</strong>
              </p>
              <ul className="text-sm text-purple-700 mt-2 space-y-1 list-disc list-inside">
                <li>Remove super admin privileges from this user</li>
                <li>They will remain a teacher with standard admin rights</li>
                <li>They will no longer be able to manage other teachers</li>
                <li>They will no longer be able to delete users</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowRemoveSuperAdminDialog(false);
                setSelectedTeacher(null);
              }}
              disabled={removingSuperAdmin}
            >
              Cancel
            </Button>
            <Button
              onClick={removeSuperAdmin}
              disabled={removingSuperAdmin}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {removingSuperAdmin ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                  Removing...
                </>
              ) : (
                <>
                  <Crown className="w-4 h-4 mr-2" />
                  Confirm Remove
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
